import { useState } from "react";
import { Pencil, Trash2, UserPlus } from "lucide-react";
import toast from "react-hot-toast";
import { useClientStore } from "../../store/useClientStore";
import EditClientModal from "./EditClientModal";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import InputField from "../ui/InputField";
import CopyButton from "../ui/CopyButton";
import { ExternalLink } from "lucide-react";

interface ClientCardProps {
  client: any;
  negotiatingBids?: any[];
}

const ClientCard = ({ client, negotiatingBids = [] }: ClientCardProps) => {
  const { deleteClient, createClientAccount } = useClientStore();
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCreateAccount = async () => {
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Invalid email format");
      return;
    }

    setIsCreating(true);
    try {
      await createClientAccount(client._id, { email, password });
      toast.success("Client account created successfully!");
      setShowAccountModal(false);
      setEmail("");
      setPassword("");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to create account");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>

      <div className="md:hidden bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{client.name}</h3>
            {client.username && <p className="text-sm text-gray-500">@{client.username}</p>}
          </div>
          <Badge status={client.leadStatus}>{client.leadStatus}</Badge>
        </div>

        <div className="space-y-2.5 text-sm">
          {client.email ? (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Email:</span>
              <div className="flex items-center gap-1">
                <span className="font-medium text-[#2462b2] truncate max-w-[140px]">{client.email}</span>
                <CopyButton text={client.email} label="Email" size={14} />
              </div>
            </div>
          ) : (
            <p className="text-gray-500 italic">No account</p>
          )}

          {client.phone && (
            <p><span className="text-gray-600">Phone:</span> <span className="font-medium">{client.phone}</span></p>
          )}
          {client.company && (
            <p><span className="text-gray-600">Company:</span> <span className="font-medium">{client.company}</span></p>
          )}
          {(client as any).location && (
            <p><span className="text-gray-600">Location:</span> <span className="font-medium">{(client as any).location}</span></p>
          )}
          {(client as any).timezone && (
            <p><span className="text-gray-600">Time Zone:</span> <span className="font-medium">{(client as any).timezone}</span></p>
          )}
          <p className="flex items-center justify-between">
            <span className="text-gray-600">Tier:</span>
            <span
              className={`px-2 py-0.5 rounded-full text-[11px] font-bold ring-1 ${
                (client.tier || "Standard") === "Premium"
                  ? "bg-amber-100 text-amber-800 ring-amber-200"
                  : (client.tier || "Standard") === "Standard"
                  ? "bg-blue-100 text-blue-700 ring-blue-200"
                  : "bg-slate-100 text-slate-700 ring-slate-200"
              }`}
            >
              {client.tier || "Standard"}
            </span>
          </p>
          <p><span className="text-gray-600">Type:</span> <span className="font-medium">{client.contactType || "Individual"}</span></p>
          {client.website && (
            <p className="flex items-center justify-between">
              <span className="text-gray-600">Website:</span>
              <a
                href={client.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2462b2] hover:underline flex items-center gap-1 text-sm font-medium"
              >
                {client.website.replace(/^https?:\/\//, "").split("/")[0]}
                <ExternalLink size={12} />
              </a>
            </p>
          )}
          <p><span className="text-gray-600">Source:</span> <span className="font-medium">{client.leadSource || "—"}</span></p>
          {negotiatingBids.length > 0 && (
            <p><span className="text-gray-600">Negotiating:</span> <span className="font-medium text-amber-700">{negotiatingBids.length} bid{negotiatingBids.length > 1 ? "s" : ""}</span></p>
          )}
          <p><span className="text-gray-600">Added:</span> <span className="font-medium">{formatDate(client.createdAt)}</span></p>
          {client.notes && (
            <p className="text-xs text-gray-600 italic bg-gray-50 p-2 rounded-lg mt-3 line-clamp-2">
              "{client.notes}"
            </p>
          )}
        </div>

        <div className="flex gap-2 mt-5">
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
            <Pencil size={15} /> Edit
          </Button>
          <Button variant="danger" size="sm" onClick={() => deleteClient(client._id)}>
            <Trash2 size={15} /> Delete
          </Button>
          {!client.userId && (
            <Button variant="primary" size="sm" onClick={() => setShowAccountModal(true)}>
              <UserPlus size={15} /> Account
            </Button>
          )}
        </div>
      </div>

      <tr className="hidden md:table-row hover:bg-gradient-to-r hover:from-[#2462b2]/5 hover:to-transparent transition-all duration-200 group border-b border-gray-100">
        <td className="px-4 py-4 text-sm font-semibold text-gray-900 align-middle whitespace-nowrap">{client.name}</td>
        <td className="px-4 py-4 text-sm text-gray-600 align-middle whitespace-nowrap min-w-[140px]">{client.username || "—"}</td>
        <td className="px-4 py-4 text-sm align-middle whitespace-nowrap">
          {client.email ? (
            <div className="flex items-center gap-1.5">
              <span className="text-[#2462b2] truncate max-w-[130px]">{client.email}</span>
              <CopyButton text={client.email} label="Email" size={14} />
            </div>
          ) : (
            <span className="text-gray-500 italic">No account</span>
          )}
        </td>
        <td className="px-4 py-4 text-sm text-gray-700 align-middle whitespace-nowrap">{client.phone || "—"}</td>
        <td className="px-4 py-4 align-middle whitespace-nowrap">
          <span
            className={`px-2 py-0.5 rounded-full text-[11px] font-bold ring-1 whitespace-nowrap ${
              (client.tier || "Standard") === "Premium"
                ? "bg-amber-100 text-amber-800 ring-amber-200"
                : (client.tier || "Standard") === "Standard"
                ? "bg-blue-100 text-blue-700 ring-blue-200"
                : "bg-slate-100 text-slate-700 ring-slate-200"
            }`}
          >
            {client.tier || "Standard"}
          </span>
        </td>
        <td className="px-4 py-4 text-sm text-gray-700 align-middle whitespace-nowrap whitespace-nowrap">
          {client.contactType || "Individual"}
        </td>
        <td className="px-4 py-4 text-xs text-gray-500 align-middle whitespace-nowrap">{client.createdAt ? new Date(client.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
        <td className="px-4 py-4 text-sm text-gray-700 align-middle whitespace-nowrap">{client.leadSource || "—"}</td>
        <td className="px-4 py-4 align-middle whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            <Badge status={client.leadStatus}>{client.leadStatus}</Badge>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ring-1 whitespace-nowrap ${
              (client as any).contactStatus === "Client"
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                : "bg-slate-100 text-slate-600 ring-slate-200"
            }`}>
              {(client as any).contactStatus || "Lead"}
            </span>
          </div>
        </td>
        <td className="px-4 py-4 text-xs text-gray-600 max-w-[120px] truncate align-middle whitespace-nowrap" title={client.notes || ""}>
          {client.notes || "—"}
        </td>
        <td className="px-4 py-4 text-sm align-middle whitespace-nowrap">
          {client.website ? (
            <a
              href={client.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2462b2] hover:underline flex items-center gap-1 text-xs font-medium"
            >
              {client.website.replace(/^https?:\/\//, "").split("/")[0]}
              <ExternalLink size={11} />
            </a>
          ) : "—"}
        </td>
        <td className="px-2 py-4 text-sm align-middle whitespace-nowrap text-center">
          {negotiatingBids.length > 0 ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700 ring-1 ring-amber-200"
              title={negotiatingBids.map((b: any) => b.jobTitle || "Untitled").join("\n")}
            >
              {negotiatingBids.length}
            </span>
          ) : (
            <span className="text-gray-300">—</span>
          )}
        </td>
        <td className="px-4 py-4 text-sm text-gray-700 align-middle whitespace-nowrap">{client.company || "—"}</td>
        <td className="px-4 py-4 text-sm text-gray-700 align-middle whitespace-nowrap">{(client as any).location || "—"}</td>
        <td className="px-4 py-4 text-sm text-gray-700 align-middle whitespace-nowrap">{((client as any).timezone || "—").replace(/_/g, " ")}</td>
        <td className="px-4 py-4 text-sm align-middle whitespace-nowrap">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} title="Edit">
              <Pencil size={15} />
            </Button>
            <Button variant="danger" size="sm" onClick={() => deleteClient(client._id)} title="Delete">
              <Trash2 size={15} />
            </Button>
            {!client.userId && (
              <Button variant="primary" size="sm" onClick={() => setShowAccountModal(true)} title="Create Account">
                <UserPlus size={15} />
              </Button>
            )}
          </div>
        </td>
      </tr>

      {isEditing && <EditClientModal client={client} onClose={() => setIsEditing(false)} />}

      <Modal isOpen={showAccountModal} onClose={() => setShowAccountModal(false)} title="Create Account">
        <div className="space-y-4">
          <InputField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="client@example.com"
            type="email"
            required
          />
          <InputField
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            type="text"
          />
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="ghost" onClick={() => setShowAccountModal(false)}>Cancel</Button>
          <Button variant="primary" isLoading={isCreating} onClick={handleCreateAccount}>
            Create Account
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ClientCard;
